<template>
  <section class="final-audio-studio">
    <div class="final-audio-studio__workspace">
      <section class="final-audio-studio__compose-panel">
        <div class="final-audio-studio__panel-head final-audio-studio__panel-head--split">
          <div class="final-audio-studio__panel-title">
            <h3>旁白片段</h3>
          </div>
          <div class="final-audio-studio__panel-cta">
            <el-button type="primary" :loading="ttsGenerating" :disabled="!finalTtsScript.trim()" @click="emit('generate-tts')">
              生成并加入时间线
            </el-button>
          </div>
        </div>

        <div class="final-audio-studio__compose-layout">
          <article class="final-audio-studio__script-card">
            <div class="final-audio-studio__script-head">
              <div>
                <strong>文案内容</strong>
              </div>
            </div>
            <el-input
              :model-value="finalTtsScript"
              type="textarea"
              :rows="9"
              maxlength="4000"
              show-word-limit
              placeholder="输入当前这段 TTS 的文案，例如：开头点题、卖点解释、结尾行动引导。"
              @update:model-value="emit('update:finalTtsScript', String($event || ''))"
            />
          </article>

          <article class="final-audio-studio__setup-card">
            <div class="final-audio-studio__setup-intro">
              <strong>生成设置</strong>
            </div>

            <div class="final-audio-studio__setup-grid">
              <label class="final-audio-studio__setup-field">
                <div class="final-audio-studio__setup-head">
                  <span class="final-audio-studio__setup-label">供应商</span>
                  <small>{{ ttsVoiceOptions.length || 0 }} 个音色</small>
                </div>
                <el-select
                  :model-value="finalTtsProvider"
                  placeholder="选择供应商"
                  @update:model-value="emit('update:finalTtsProvider', String($event || ''))"
                >
                  <el-option v-for="item in providerOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </label>

              <label class="final-audio-studio__setup-field">
                <div class="final-audio-studio__setup-head">
                  <span class="final-audio-studio__setup-label">音色</span>
                  <small>当前选择</small>
                </div>
                <el-select
                  :model-value="finalTtsVoiceId"
                  :loading="audioOptionsLoading"
                  filterable
                  placeholder="选择音色"
                  @update:model-value="emit('update:finalTtsVoiceId', String($event || ''))"
                >
                  <el-option v-for="item in ttsVoiceOptions" :key="item.value" :label="item.label" :value="item.value">
                    <div class="final-audio-studio__voice-option">
                      <strong>{{ item.label }}</strong>
                      <small v-if="item.description">{{ item.description }}</small>
                    </div>
                  </el-option>
                </el-select>
              </label>

              <label class="final-audio-studio__setup-field">
                <div class="final-audio-studio__setup-head">
                  <span class="final-audio-studio__setup-label">开始时间</span>
                  <span class="final-audio-studio__setup-value">{{ draftStartTimeLabel }}</span>
                </div>
                <div class="final-audio-studio__number-shell">
                  <el-input-number
                    :model-value="finalTtsStartMs / 1000"
                    :min="0"
                    :step="0.1"
                    :precision="1"
                    controls-position="right"
                    @update:model-value="emit('update:finalTtsStartMs', Math.max(0, Math.round(Number($event || 0) * 1000)))"
                  />
                </div>
              </label>

              <label class="final-audio-studio__setup-field">
                <div class="final-audio-studio__setup-head">
                  <span class="final-audio-studio__setup-label">语速</span>
                  <span class="final-audio-studio__setup-value">{{ finalTtsSpeed.toFixed(1) }}x</span>
                </div>
                <el-slider
                  :model-value="finalTtsSpeed"
                  :min="0.6"
                  :max="1.6"
                  :step="0.1"
                  @update:model-value="emit('update:finalTtsSpeed', Number($event || 1))"
                />
              </label>
            </div>
          </article>
        </div>
      </section>

      <aside class="final-audio-studio__arrange-panel">
        <div class="final-audio-studio__panel-head">
          <div class="final-audio-studio__panel-title">
            <h3>时间线与素材</h3>
          </div>
        </div>

        <article class="final-audio-studio__asset-card" :class="{ 'final-audio-studio__asset-card--filled': hasNarrationSegments }">
          <div class="final-audio-studio__asset-head">
            <div>
              <span>旁白时间线</span>
              <strong>{{ narrationTimelineTitle }}</strong>
            </div>
            <div class="final-audio-studio__asset-actions">
              <el-button text @click="emit('open-audio-library', 'narration')">素材中心</el-button>
              <el-button v-if="hasNarrationSegments" text @click="emit('clear-generated-tts')">清空时间线</el-button>
            </div>
          </div>
          <div class="final-audio-studio__summary-bar final-audio-studio__summary-bar--tight">
            <span class="final-audio-studio__info-pill">{{ timelineSummaryText }}</span>
            <span v-if="timelineConflictText" class="final-audio-studio__info-pill final-audio-studio__info-pill--danger">需要先消除重叠</span>
          </div>
          <div v-if="timelineConflictText" class="final-audio-studio__timeline-warning">
            <strong>时间线冲突</strong>
            <span>{{ timelineConflictText }}</span>
          </div>
          <div v-if="sortedTimelineSegments.length" class="final-audio-studio__timeline-list">
            <article v-for="(segment, index) in sortedTimelineSegments" :key="segment.id" class="final-audio-studio__timeline-item">
              <div class="final-audio-studio__timeline-item-head">
                <div class="final-audio-studio__timeline-title-block">
                  <span class="final-audio-studio__timeline-index">片段 {{ index + 1 }}</span>
                  <strong>{{ formatSegmentTitle(segment, index) }}</strong>
                  <small>{{ formatSegmentProvider(segment) }}</small>
                </div>
                <el-dropdown trigger="click" @command="(command) => handleTimelineMenuCommand(segment.id, command)">
                  <button type="button" class="final-audio-studio__more-button">更多</button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item v-if="segment.audio?.url" command="download">下载音频</el-dropdown-item>
                      <el-dropdown-item :divided="Boolean(segment.audio?.url)" command="remove">删除片段</el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
              <div class="final-audio-studio__timeline-edit-row">
                <label class="final-audio-studio__timeline-start final-audio-studio__timeline-start--compact">
                  <span>开始时间</span>
                  <div class="final-audio-studio__number-shell">
                    <el-input-number
                      :model-value="segment.start_ms / 1000"
                      :min="0"
                      :step="0.1"
                      :precision="1"
                      controls-position="right"
                      @update:model-value="emit('update:ttsSegmentStartMs', { id: segment.id, startMs: Math.max(0, Math.round(Number($event || 0) * 1000)) })"
                    />
                  </div>
                </label>
                <div class="final-audio-studio__timeline-fact final-audio-studio__timeline-fact--highlight">
                  <span>时长</span>
                  <strong>{{ formatSegmentDuration(segment) }}</strong>
                </div>
              </div>
              <p class="final-audio-studio__timeline-script">{{ segment.script || '未填写文案' }}</p>
              <div v-if="segment.audio?.url" class="final-audio-studio__timeline-player-wrap">
                <audio class="final-audio-studio__audio-player" :src="segment.audio.url" controls preload="metadata" />
              </div>
            </article>
          </div>
        </article>

        <article class="final-audio-studio__asset-card" :class="{ 'final-audio-studio__asset-card--filled': uploadedFinalBgmAudio?.url }">
          <div class="final-audio-studio__asset-head">
            <div>
              <span>背景音乐</span>
              <strong>{{ uploadedFinalBgmAudio?.name || 'BGM 还没准备好' }}</strong>
            </div>
            <div class="final-audio-studio__asset-actions">
              <el-button text @click="emit('open-audio-library', 'bgm')">素材中心</el-button>
              <el-button text :loading="bgmUploading" @click="emit('trigger-bgm-upload')">{{ uploadedFinalBgmAudio?.url ? '更换' : '上传' }}</el-button>
              <el-button v-if="uploadedFinalBgmAudio?.url" text @click="emit('download-uploaded-bgm')">下载</el-button>
              <el-button v-if="uploadedFinalBgmAudio?.url" text @click="emit('clear-uploaded-bgm')">清空</el-button>
            </div>
          </div>
          <audio v-if="uploadedFinalBgmAudio?.url" class="final-audio-studio__audio-player" :src="uploadedFinalBgmAudio.url" controls preload="metadata" />
        </article>
      </aside>
    </div>

    <section class="final-audio-studio__preview-deck" :class="{ 'final-audio-studio__asset-card--filled': audioMixPreviewVideo?.url }">
      <div class="final-audio-studio__panel-head final-audio-studio__panel-head--split">
        <div class="final-audio-studio__panel-title">
          <h3>混音预览</h3>
        </div>
        <div class="final-audio-studio__mix-actions">
          <el-button type="primary" :loading="finalAudioMixing" :disabled="!hasMixInput || Boolean(timelineConflictText)" @click="emit('generate-mix-preview')">
            生成混音预览
          </el-button>
          <el-button v-if="audioMixPreviewVideo?.url" plain @click="emit('download-preview-video')">下载预览</el-button>
          <el-button v-if="audioMixPreviewVideo?.url" :disabled="isPreviewApplied" @click="emit('apply-preview-as-delivery')">
            {{ isPreviewApplied ? '已设为交付版' : '设为当前交付版' }}
          </el-button>
        </div>
      </div>

      <div class="final-audio-studio__summary-bar final-audio-studio__summary-bar--tight">
        <span class="final-audio-studio__info-pill">{{ mixReadinessText }}</span>
        <span class="final-audio-studio__info-pill">{{ previewSummaryText }}</span>
        <span v-if="isPreviewApplied" class="final-audio-studio__info-pill final-audio-studio__info-pill--accent">当前预览已应用</span>
      </div>

      <div class="final-audio-studio__preview-grid">
        <div class="final-audio-studio__preview-settings">
          <div class="final-audio-studio__mini-head">
            <strong>预览设置</strong>
            <span>默认只保留最常用的开关，需要时再展开高级参数。</span>
          </div>

          <div class="final-audio-studio__toggle-strip">
            <label class="final-audio-studio__toggle-card">
              <div>
                <strong>保留原视频原声</strong>
                <small>保留环境声或对白原声。</small>
              </div>
              <el-switch
                :model-value="audioMixControls.keep_original_audio"
                @update:model-value="updateAudioMixControl('keep_original_audio', Boolean($event))"
              />
            </label>

            <label class="final-audio-studio__toggle-card">
              <div>
                <strong>BGM 自动压低</strong>
                <small>有人声时自动让 BGM 后退。</small>
              </div>
              <el-switch
                :model-value="audioMixControls.ducking_enabled"
                @update:model-value="updateAudioMixControl('ducking_enabled', Boolean($event))"
              />
            </label>
          </div>

          <button type="button" class="final-audio-studio__advanced-toggle" @click="showAdvancedMixControls = !showAdvancedMixControls">
            {{ showAdvancedMixControls ? '收起高级混音参数' : '展开高级混音参数' }}
          </button>

          <div v-if="showAdvancedMixControls" class="final-audio-studio__mix-slider-grid">
            <label class="final-audio-studio__mix-slider-card">
              <div class="final-audio-studio__field-head">
                <span>旁白音量</span>
                <strong>{{ audioMixControls.narration_volume.toFixed(2) }}</strong>
              </div>
              <el-slider
                :model-value="audioMixControls.narration_volume"
                :min="0"
                :max="1.5"
                :step="0.05"
                @update:model-value="updateAudioMixControl('narration_volume', Number($event || 0))"
              />
            </label>

            <label class="final-audio-studio__mix-slider-card">
              <div class="final-audio-studio__field-head">
                <span>BGM 音量</span>
                <strong>{{ audioMixControls.bgm_volume.toFixed(2) }}</strong>
              </div>
              <el-slider
                :model-value="audioMixControls.bgm_volume"
                :min="0"
                :max="1.5"
                :step="0.05"
                @update:model-value="updateAudioMixControl('bgm_volume', Number($event || 0))"
              />
            </label>

            <label class="final-audio-studio__mix-slider-card" :class="{ 'final-audio-studio__mix-slider-card--disabled': !audioMixControls.keep_original_audio }">
              <div class="final-audio-studio__field-head">
                <span>原声音量</span>
                <strong>{{ audioMixControls.original_audio_volume.toFixed(2) }}</strong>
              </div>
              <el-slider
                :model-value="audioMixControls.original_audio_volume"
                :min="0"
                :max="1.5"
                :step="0.05"
                :disabled="!audioMixControls.keep_original_audio"
                @update:model-value="updateAudioMixControl('original_audio_volume', Number($event || 0))"
              />
            </label>
          </div>
        </div>

        <div v-if="audioMixPreviewVideo?.url" class="final-audio-studio__preview-stage">
          <video class="final-audio-studio__preview-player" :src="audioMixPreviewVideo.url" controls preload="metadata" />
        </div>
        <div v-else class="final-audio-studio__preview-empty">
          <strong>还没有混音预览</strong>
          <p>{{ previewStatusText }}</p>
        </div>
      </div>
    </section>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { findNarrationTimelineConflict, formatTimelineDuration, formatTimelineTime, sortNarrationTimelineSegments } from './finalAudioTimeline'
import type { AudioMixControlDraft, FinalAudioNarrationSegmentDraft, UploadedWorkspaceAudio, UploadedWorkspaceVideo } from './types'

interface SelectOption {
  label: string
  value: string
}

const props = defineProps<{
  finalVoiceoverMode: string
  finalVoiceoverTone: string
  finalBgmPlan: string
  finalVoiceoverModeOptions: ReadonlyArray<SelectOption>
  finalVoiceoverToneOptions: ReadonlyArray<SelectOption>
  finalBgmPlanOptions: ReadonlyArray<SelectOption>
  finalTtsProvider: string
  finalTtsVoiceId: string
  finalTtsSpeed: number
  finalTtsScript: string
  finalTtsStartMs: number
  audioOptionsLoading: boolean
  ttsGenerating: boolean
  bgmUploading: boolean
  finalAudioMixing: boolean
  audioMixControls: AudioMixControlDraft
  ttsVoiceOptions: ReadonlyArray<{ label: string; value: string; description?: string | null }>
  finalTtsTimelineSegments: ReadonlyArray<FinalAudioNarrationSegmentDraft>
  uploadedFinalBgmAudio: UploadedWorkspaceAudio | null
  audioMixPreviewVideo: UploadedWorkspaceVideo | null
  audioMixDeliveryVideo: UploadedWorkspaceVideo | null
}>()

const emit = defineEmits<{
  (event: 'update:finalVoiceoverMode', value: string): void
  (event: 'update:finalVoiceoverTone', value: string): void
  (event: 'update:finalBgmPlan', value: string): void
  (event: 'update:finalTtsProvider', value: string): void
  (event: 'update:finalTtsVoiceId', value: string): void
  (event: 'update:finalTtsSpeed', value: number): void
  (event: 'update:finalTtsScript', value: string): void
  (event: 'update:finalTtsStartMs', value: number): void
  (event: 'update:ttsSegmentStartMs', value: { id: string; startMs: number }): void
  (event: 'update:audioMixControls', value: AudioMixControlDraft): void
  (event: 'open-audio-library', role: 'narration' | 'bgm'): void
  (event: 'generate-tts'): void
  (event: 'trigger-bgm-upload'): void
  (event: 'generate-mix-preview'): void
  (event: 'apply-preview-as-delivery'): void
  (event: 'clear-generated-tts'): void
  (event: 'remove-tts-segment', segmentId: string): void
  (event: 'download-tts-segment', segmentId: string): void
  (event: 'clear-uploaded-bgm'): void
  (event: 'download-uploaded-bgm'): void
  (event: 'download-preview-video'): void
}>()

const providerOptions = [
  { label: '阿里云智能语音', value: 'aliyun' },
  { label: '火山引擎语音', value: 'volcengine' },
  { label: 'Azure TTS', value: 'azure' },
]

const providerLabel = computed(() => {
  if (props.finalTtsProvider === 'aliyun') return '阿里云智能语音'
  if (props.finalTtsProvider === 'azure') return 'Azure TTS'
  if (props.finalTtsProvider === 'volcengine') return '火山引擎语音'
  if (props.finalTtsProvider === 'elevenlabs') return 'ElevenLabs'
  return props.finalTtsProvider || '未配置'
})

const selectedVoice = computed(() => props.ttsVoiceOptions.find((item) => item.value === props.finalTtsVoiceId) || null)

const selectedVoiceLabel = computed(() => selectedVoice.value?.label || props.finalTtsVoiceId || '未选择音色')

const selectedVoiceDescription = computed(() => selectedVoice.value?.description || '选择音色后生成，可直接在右侧试听')

const sortedTimelineSegments = computed(() => sortNarrationTimelineSegments([...props.finalTtsTimelineSegments]))

const hasNarrationSegments = computed(() => sortedTimelineSegments.value.length > 0)

const hasMixInput = computed(() => Boolean(sortedTimelineSegments.value.some((segment) => segment.audio?.url) || props.uploadedFinalBgmAudio?.url))

const draftStartTimeLabel = computed(() => formatTimelineTime(props.finalTtsStartMs))

const narrationTimelineTitle = computed(() => {
  if (!sortedTimelineSegments.value.length) {
    return '尚未加入片段'
  }
  return `${sortedTimelineSegments.value.length} 段旁白等待混音`
})

const timelineConflictText = computed(() => {
  const conflict = findNarrationTimelineConflict(sortedTimelineSegments.value)
  if (!conflict) {
    return ''
  }

  return `${formatSegmentTitle(conflict.current)} 与 ${formatSegmentTitle(conflict.previous)} 在 ${formatTimelineTime(conflict.current.start_ms)} 发生重叠。`
})

const narrationTimelineDescription = computed(() => {
  if (timelineConflictText.value) {
    return '当前时间线存在重叠片段，混音预览前必须先消除冲突。'
  }
  if (sortedTimelineSegments.value.length) {
    return '每一段都会按开始时间落到视频时间线上，再统一进入混音预览。'
  }
  return '先写一段文案、指定开始时间，再生成对应的旁白片段。'
})

const isPreviewApplied = computed(() => {
  return Boolean(
    props.audioMixPreviewVideo?.url
    && props.audioMixDeliveryVideo?.url
    && props.audioMixPreviewVideo.url === props.audioMixDeliveryVideo.url,
  )
})

const showAdvancedMixControls = ref(false)

const timelineSummaryText = computed(() => {
  if (timelineConflictText.value) {
    return '时间线有冲突'
  }
  if (sortedTimelineSegments.value.length) {
    return `${sortedTimelineSegments.value.length} 段旁白已排好时间`
  }
  return '还没有旁白片段'
})

const bgmStatusText = computed(() => {
  if (props.uploadedFinalBgmAudio?.url) {
    return 'BGM 已准备好，可以直接参与混音预览；如果不满意，随时更换或清空。'
  }
  return '支持上传一条背景音乐，或者先不加 BGM，直接测试旁白与原声关系。'
})

const mixReadinessText = computed(() => {
  if (timelineConflictText.value) {
    return '先处理时间线重叠，再生成预览'
  }
  if (hasMixInput.value) {
    return '素材已具备，可以生成预览'
  }
  return '至少准备一段旁白或一条 BGM'
})

const previewSummaryText = computed(() => {
  if (isPreviewApplied.value) {
    return '预览已确认并应用到交付版'
  }
  if (props.audioMixPreviewVideo?.url) {
    return '预览已生成，等待你确认是否应用'
  }
  return '还没有混音预览'
})

const previewStatusText = computed(() => {
  if (isPreviewApplied.value) {
    return '当前预览已经被设为正式交付版。你仍然可以继续调参数并生成新的预览。'
  }
  if (props.audioMixPreviewVideo?.url) {
    return '预览已生成。请先看带画面的试听结果，再决定是否把这版提升为当前交付版。'
  }
  return '这里生成的是带画面的混音预览，不会自动替换当前交付视频。'
})

function updateAudioMixControl<Key extends keyof AudioMixControlDraft>(key: Key, value: AudioMixControlDraft[Key]) {
  emit('update:audioMixControls', {
    ...props.audioMixControls,
    [key]: value,
  })
}

function handleTimelineMenuCommand(segmentId: string, command: string | number | object) {
  if (command === 'download') {
    emit('download-tts-segment', segmentId)
    return
  }

  if (command === 'remove') {
    emit('remove-tts-segment', segmentId)
  }
}

function formatSegmentTitle(segment: FinalAudioNarrationSegmentDraft, index = 0) {
  return segment.script || segment.audio?.name || `旁白片段 ${index + 1}`
}

function formatSegmentDuration(segment: FinalAudioNarrationSegmentDraft) {
  return formatTimelineDuration(segment.audio?.duration_seconds)
}

function formatSegmentProvider(segment: FinalAudioNarrationSegmentDraft) {
  if (segment.provider === 'aliyun') return '阿里云智能语音'
  if (segment.provider === 'azure') return 'Azure TTS'
  if (segment.provider === 'volcengine') return '火山引擎语音'
  return segment.audio?.name ? '已选素材' : '待生成'
}

</script>

<style scoped>
.final-audio-studio,
.final-audio-studio__workspace,
.final-audio-studio__compose-panel,
.final-audio-studio__arrange-panel,
.final-audio-studio__compose-layout,
.final-audio-studio__preview-deck,
.final-audio-studio__preview-settings,
.final-audio-studio__panel-title,
.final-audio-studio__setup-card,
.final-audio-studio__timeline-title-block {
  display: grid;
  gap: 16px;
}

.final-audio-studio {
  gap: 20px;
  padding: 24px;
  border-radius: 32px;
  background:
    radial-gradient(circle at top right, rgba(59, 130, 246, 0.16), transparent 28%),
    radial-gradient(circle at bottom left, rgba(45, 212, 191, 0.12), transparent 24%),
    linear-gradient(150deg, rgba(255, 255, 255, 0.98), rgba(244, 250, 255, 0.96));
  border: 1px solid rgba(186, 230, 253, 0.72);
  box-shadow: 0 28px 64px rgba(15, 23, 42, 0.08);
}

.final-audio-studio__workspace {
  grid-template-columns: minmax(0, 1.18fr) minmax(320px, 0.82fr);
  align-items: start;
}

.final-audio-studio__compose-panel,
.final-audio-studio__arrange-panel,
.final-audio-studio__preview-deck {
  padding: 20px;
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(226, 232, 240, 0.92);
  box-shadow: 0 18px 34px rgba(15, 23, 42, 0.05);
}

.final-audio-studio__compose-panel {
  background:
    radial-gradient(circle at top right, rgba(59, 130, 246, 0.12), transparent 34%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(248, 250, 252, 0.98));
}

.final-audio-studio__preview-deck {
  gap: 18px;
  background:
    radial-gradient(circle at top right, rgba(20, 184, 166, 0.14), transparent 24%),
    linear-gradient(180deg, rgba(248, 250, 252, 0.96), rgba(255, 255, 255, 0.92));
}

.final-audio-studio__panel-head,
.final-audio-studio__asset-head,
.final-audio-studio__mix-actions,
.final-audio-studio__panel-cta,
.final-audio-studio__mini-head {
  display: flex;
  gap: 12px;
}

.final-audio-studio__panel-head,
.final-audio-studio__asset-head,
.final-audio-studio__mix-actions,
.final-audio-studio__mini-head {
  justify-content: space-between;
  align-items: start;
}

.final-audio-studio__panel-head--split {
  align-items: end;
}

.final-audio-studio__panel-cta {
  flex-direction: column;
  align-items: flex-end;
}

.final-audio-studio__panel-badge {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  color: #0f4c81;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.final-audio-studio__panel-title h3,
.final-audio-studio__mini-head strong,
.final-audio-studio__asset-card strong,
.final-audio-studio__mix-slider-card strong,
.final-audio-studio__toggle-card strong,
.final-audio-studio__preview-empty strong,
.final-audio-studio__control-card strong {
  margin: 0;
  color: #0f172a;
}

.final-audio-studio__panel-title h3 {
  font-size: 24px;
  line-height: 1.15;
}

.final-audio-studio__panel-title p,
.final-audio-studio__asset-card p,
.final-audio-studio__preview-empty p,
.final-audio-studio__toggle-card small,
.final-audio-studio__mini-head span {
  margin: 0;
  color: #64748b;
  line-height: 1.65;
}

.final-audio-studio__asset-card span,
.final-audio-studio__asset-card span,
.final-audio-studio__preview-deck span,
.final-audio-studio__control-card span,
.final-audio-studio__field-head span {
  color: #64748b;
  font-size: 12px;
}

.final-audio-studio__asset-card,
.final-audio-studio__script-card,
.final-audio-studio__control-card {
  padding: 18px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.84);
  border: 1px solid rgba(226, 232, 240, 0.92);
}

.final-audio-studio__compose-layout {
  grid-template-columns: 1fr;
  align-items: start;
  gap: 18px;
}

.final-audio-studio__script-card,
.final-audio-studio__setup-card,
.final-audio-studio__preview-settings {
  display: grid;
  gap: 14px;
}

.final-audio-studio__setup-card {
  gap: 18px;
  padding: 18px;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(249, 250, 251, 0.98));
  border: 1px solid rgba(226, 232, 240, 0.96);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.final-audio-studio__script-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: start;
}

.final-audio-studio__script-head strong {
  display: block;
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  line-height: 1.2;
}

.final-audio-studio__script-head p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
}

.final-audio-studio__setup-intro {
  display: grid;
  gap: 4px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.78);
}

.final-audio-studio__setup-intro strong {
  display: block;
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  line-height: 1.15;
}

.final-audio-studio__setup-intro p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
}

.final-audio-studio__setup-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.final-audio-studio__setup-field {
  display: grid;
  gap: 8px;
  min-width: 0;
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(226, 232, 240, 0.9);
}

.final-audio-studio__setup-label,
.final-audio-studio__timeline-index {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.final-audio-studio__setup-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: baseline;
}

.final-audio-studio__setup-field small,
.final-audio-studio__timeline-title-block small {
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.5;
}

.final-audio-studio__setup-value {
  color: #0f172a;
  font-size: 14px;
  font-weight: 800;
  white-space: nowrap;
}

.final-audio-studio__number-shell {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 48px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid rgba(203, 213, 225, 0.95);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.final-audio-studio :deep(.el-input-number) {
  width: 100%;
}

.final-audio-studio :deep(.el-input-number .el-input__wrapper) {
  border-radius: 14px;
  background: #fff;
  box-shadow: none;
}

.final-audio-studio :deep(.el-input-number.is-controls-right .el-input-number__decrease),
.final-audio-studio :deep(.el-input-number.is-controls-right .el-input-number__increase) {
  width: 36px;
  border-left: 1px solid rgba(226, 232, 240, 0.95);
  background: linear-gradient(180deg, #ffffff, #f8fafc);
  color: #475569;
}

.final-audio-studio :deep(.el-input-number .el-input__inner) {
  color: #0f172a;
  font-weight: 600;
}

.final-audio-studio__controls-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.final-audio-studio__control-card {
  display: grid;
  gap: 10px;
}

.final-audio-studio__control-card--wide {
  grid-column: span 2;
}

.final-audio-studio__summary-bar,
.final-audio-studio__toggle-strip,
.final-audio-studio__mix-slider-grid {
  gap: 12px;
}

.final-audio-studio__summary-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.final-audio-studio__toggle-strip {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.final-audio-studio__mix-slider-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.final-audio-studio__toggle-card,
.final-audio-studio__mix-slider-card,
.final-audio-studio__preview-stage,
.final-audio-studio__preview-empty {
  display: grid;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(226, 232, 240, 0.92);
}

.final-audio-studio__toggle-card {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
}

.final-audio-studio__advanced-toggle {
  width: fit-content;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(191, 219, 254, 0.9);
  background: rgba(239, 246, 255, 0.9);
  color: #1d4ed8;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.final-audio-studio__mix-slider-card--disabled {
  opacity: 0.68;
}

.final-audio-studio__field-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}

.final-audio-studio__field-head small {
  color: #94a3b8;
  font-size: 12px;
}

.final-audio-studio__asset-card,
.final-audio-studio__arrange-panel {
  display: grid;
  gap: 12px;
}

.final-audio-studio__arrange-panel {
  align-content: start;
}

.final-audio-studio__asset-card {
  box-shadow: 0 14px 26px rgba(15, 23, 42, 0.04);
}

.final-audio-studio__timeline-warning,
.final-audio-studio__timeline-item {
  display: grid;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid rgba(226, 232, 240, 0.92);
  background: rgba(248, 250, 252, 0.88);
}

.final-audio-studio__timeline-warning {
  border-color: rgba(239, 68, 68, 0.28);
  background: rgba(254, 242, 242, 0.96);
}

.final-audio-studio__timeline-warning strong {
  color: #b91c1c;
}

.final-audio-studio__timeline-list {
  display: grid;
  gap: 12px;
}

.final-audio-studio__timeline-item-head,
.final-audio-studio__timeline-edit-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.final-audio-studio__more-button {
  padding: 8px 12px;
  border: 1px solid rgba(226, 232, 240, 0.96);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  color: #475569;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.final-audio-studio__more-button:hover {
  border-color: rgba(191, 219, 254, 0.96);
  color: #1d4ed8;
}

.final-audio-studio__timeline-title-block {
  gap: 4px;
}

.final-audio-studio__timeline-title-block strong {
  font-size: 17px;
  line-height: 1.35;
}

.final-audio-studio__timeline-edit-row {
  flex-wrap: wrap;
}

.final-audio-studio__timeline-start,
.final-audio-studio__timeline-fact {
  display: grid;
  gap: 6px;
}

.final-audio-studio__timeline-start--compact {
  min-width: 170px;
}

.final-audio-studio__timeline-fact strong {
  color: #0f172a;
}

.final-audio-studio__timeline-fact--highlight {
  min-width: 108px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(226, 232, 240, 0.82);
}

.final-audio-studio__timeline-script {
  margin: 0;
  color: #475569;
  font-size: 14px;
  line-height: 1.7;
}

.final-audio-studio__timeline-player-wrap {
  padding-top: 2px;
}

.final-audio-studio__asset-card--filled {
  border-color: rgba(125, 211, 252, 0.72);
  box-shadow: 0 18px 32px rgba(14, 116, 144, 0.08);
}

.final-audio-studio__asset-actions,
.final-audio-studio__mix-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.final-audio-studio__info-pill {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  color: #334155;
  font-size: 12px;
  font-weight: 700;
}

.final-audio-studio__info-pill--accent {
  background: rgba(56, 189, 248, 0.12);
  color: #0f4c81;
}

.final-audio-studio__info-pill--danger {
  background: rgba(254, 226, 226, 0.92);
  color: #b91c1c;
}

.final-audio-studio__voice-option {
  display: grid;
  gap: 4px;
}

.final-audio-studio__voice-option strong {
  color: #0f172a;
}

.final-audio-studio__voice-option small {
  color: #64748b;
}

.final-audio-studio__audio-player {
  width: 100%;
  height: 40px;
  border-radius: 12px;
  background: rgba(248, 250, 252, 0.92);
}

.final-audio-studio__preview-grid {
  display: grid;
  grid-template-columns: minmax(280px, 0.76fr) minmax(0, 1.24fr);
  gap: 16px;
  align-items: start;
}

.final-audio-studio__preview-player {
  width: 100%;
  min-height: 300px;
  border-radius: 18px;
  background: linear-gradient(135deg, #0f172a, #1e293b 62%, #334155);
  object-fit: cover;
}

.final-audio-studio__preview-empty {
  min-height: 300px;
  place-items: center;
  padding: 20px;
  text-align: center;
  border-radius: 18px;
  background: linear-gradient(180deg, #eef2f7 0%, #dde5ef 100%);
}

.final-audio-studio__preview-stage {
  padding: 12px;
  background: rgba(255, 255, 255, 0.74);
}

@media (max-width: 1080px) {
  .final-audio-studio__workspace,
  .final-audio-studio__preview-grid,
  .final-audio-studio__setup-grid,
  .final-audio-studio__toggle-strip,
  .final-audio-studio__controls-grid,
  .final-audio-studio__mix-slider-grid {
    grid-template-columns: 1fr;
  }

  .final-audio-studio__control-card--wide {
    grid-column: auto;
  }

}

@media (max-width: 768px) {
  .final-audio-studio {
    padding: 18px;
  }

  .final-audio-studio__panel-title h3 {
    font-size: 24px;
  }

  .final-audio-studio__panel-head,
  .final-audio-studio__asset-head,
  .final-audio-studio__mix-actions,
  .final-audio-studio__mini-head,
  .final-audio-studio__panel-head--split {
    flex-direction: column;
    align-items: stretch;
  }

  .final-audio-studio__setup-intro,
  .final-audio-studio__script-head {
    flex-direction: column;
    align-items: stretch;
  }

  .final-audio-studio__panel-cta {
    align-items: stretch;
  }
}
</style>